import type {
  Page,
  PurchaseListItem,
  Supplier,
  SupplierAccountSummary,
  SupplierGroup,
  SupplierGroupInput,
  SupplierGroupUpdate,
  SupplierInput,
  SupplierListItem,
  SupplierReturnListItem,
  SupplierSummary,
  SupplierUpdate,
} from '@sahab/contracts'
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type DownloadedFile } from '../../lib/api'
import type { Language } from '../../lib/format'
import { purchasingKeys, type SupplierPaymentListItem } from '../purchasing/queries'
import { SUPPLIER_BALANCE_FILTERS, type SupplierBalanceView, type SupplierListParams } from './search'

export const supplierKeys = {
  all: ['suppliers'] as const,
  lists: () => [...supplierKeys.all, 'list'] as const,
  list: (query: SupplierListParams) => [...supplierKeys.lists(), query] as const,
  summary: () => [...supplierKeys.all, 'summary'] as const,
  detail: (id: string) => [...supplierKeys.all, 'detail', id] as const,
}

/**
 * The profile's figures move with every purchasing document, so they live under the purchasing
 * keys: any posting, allocation or refund refreshes them with the lists.
 */
export const supplierAccountKeys = {
  summary: (id: string) => [...purchasingKeys.all, 'supplier-summary', id] as const,
  recent: (id: string) => [...purchasingKeys.all, 'supplier-recent', id] as const,
  /** List counts per balance view (We owe, Overdue, Unapplied credit). */
  balanceCounts: () => [...purchasingKeys.all, 'supplier-balance-counts'] as const,
}

export const groupKeys = {
  all: ['supplier-groups'] as const,
}

export function supplierListQueryOptions(query: SupplierListParams) {
  return queryOptions({
    queryKey: supplierKeys.list(query),
    queryFn: ({ signal }) =>
      api.get<Page<SupplierListItem>>('/suppliers', {
        signal,
        query: { page: query.page, pageSize: query.pageSize, ...listFilters(query) },
      }),
    // Keep the previous page on screen while the next one loads (no flash to skeletons).
    placeholderData: keepPreviousData,
  })
}

function listFilters(query: SupplierListParams) {
  return { sort: query.sort, q: query.q, view: query.view, groupId: query.groupId, balance: query.balance }
}

/**
 * `GET /suppliers/export`: every supplier matching the list's filters and sort (not only this page),
 * as a CSV file with headers in the UI language. Balance columns come only with purchasing.read.
 */
export function exportSuppliers(query: SupplierListParams, language: Language): Promise<DownloadedFile> {
  return api.download('/suppliers/export', {
    query: { format: 'csv', lang: language, ...listFilters(query) },
    fallbackName: 'suppliers.csv',
  })
}

/**
 * How many current suppliers each balance view holds, for the view tabs (purchasing.read only): the
 * list endpoint's `total` for one-row pages.
 */
export const supplierBalanceCountsQueryOptions = queryOptions({
  queryKey: supplierAccountKeys.balanceCounts(),
  queryFn: async ({ signal }): Promise<Record<SupplierBalanceView, number>> => {
    const totals = await Promise.all(
      SUPPLIER_BALANCE_FILTERS.map((balance) =>
        api
          .get<Page<SupplierListItem>>('/suppliers', { signal, query: { balance, pageSize: 1 } })
          .then((page) => [balance, page.total] as const),
      ),
    )
    return Object.fromEntries(totals) as Record<SupplierBalanceView, number>
  },
  staleTime: 30_000,
})

/** The profile's key figures, tab counts and attention counts in one call (purchasing.read). */
export function supplierAccountSummaryQueryOptions(id: string) {
  return queryOptions({
    queryKey: supplierAccountKeys.summary(id),
    queryFn: ({ signal }) =>
      api.get<SupplierAccountSummary>(`/suppliers/${encodeURIComponent(id)}/summary`, { signal }),
  })
}

export type RecentDocument =
  | { kind: 'purchase'; date: string; doc: PurchaseListItem }
  | { kind: 'payment'; date: string; doc: SupplierPaymentListItem }
  | { kind: 'return'; date: string; doc: SupplierReturnListItem }

/** How many recent documents the Overview lists. */
export const RECENT_DOCUMENTS = 6

/** The supplier's newest purchases, payments and returns, merged newest first (Overview). */
export function supplierRecentDocumentsQueryOptions(id: string) {
  return queryOptions({
    queryKey: supplierAccountKeys.recent(id),
    queryFn: async ({ signal }): Promise<RecentDocument[]> => {
      const page = { supplierId: id, pageSize: RECENT_DOCUMENTS }
      const [purchases, payments, returns] = await Promise.all([
        api.get<Page<PurchaseListItem>>('/purchasing/purchases', {
          signal,
          query: { ...page, sort: '-purchaseDate' },
        }),
        api.get<Page<SupplierPaymentListItem>>('/purchasing/payments', {
          signal,
          query: { ...page, sort: '-paymentDate' },
        }),
        api.get<Page<SupplierReturnListItem>>('/purchasing/returns', {
          signal,
          query: { ...page, sort: '-returnDate' },
        }),
      ])
      const all: RecentDocument[] = [
        ...purchases.items.map((doc) => ({ kind: 'purchase' as const, date: doc.purchaseDate, doc })),
        ...payments.items.map((doc) => ({ kind: 'payment' as const, date: doc.paymentDate, doc })),
        ...returns.items.map((doc) => ({ kind: 'return' as const, date: doc.returnDate, doc })),
      ]
      return all.sort((a, b) => b.date.localeCompare(a.date)).slice(0, RECENT_DOCUMENTS)
    },
  })
}

export const supplierSummaryQueryOptions = queryOptions({
  queryKey: supplierKeys.summary(),
  queryFn: ({ signal }) => api.get<SupplierSummary>('/suppliers/summary', { signal }),
})

export function supplierQueryOptions(id: string) {
  return queryOptions({
    queryKey: supplierKeys.detail(id),
    queryFn: ({ signal }) => api.get<Supplier>(`/suppliers/${encodeURIComponent(id)}`, { signal }),
  })
}

export const supplierGroupsQueryOptions = queryOptions({
  queryKey: groupKeys.all,
  queryFn: ({ signal }) => api.get<SupplierGroup[]>('/supplier-groups', { signal }),
  staleTime: 5 * 60_000,
})

/** Lists, counts and group member counts all depend on suppliers: refresh them together. */
function useInvalidateSupplierViews() {
  const queryClient = useQueryClient()
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: supplierKeys.lists() }),
      queryClient.invalidateQueries({ queryKey: supplierKeys.summary() }),
      queryClient.invalidateQueries({ queryKey: groupKeys.all }),
    ])
}

export function useCreateSupplier() {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateSupplierViews()
  return useMutation({
    mutationFn: (input: SupplierInput) => api.post<Supplier>('/suppliers', input),
    onSuccess: (supplier) => {
      queryClient.setQueryData(supplierKeys.detail(supplier.id), supplier)
      void invalidate()
    },
  })
}

export function useUpdateSupplier(id: string) {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateSupplierViews()
  return useMutation({
    mutationFn: (input: SupplierUpdate) => api.put<Supplier>(`/suppliers/${encodeURIComponent(id)}`, input),
    onSuccess: (supplier) => {
      queryClient.setQueryData(supplierKeys.detail(supplier.id), supplier)
      void invalidate()
    },
  })
}

/** Archive or restore any supplier (the list's row actions and the profile's More menu). */
export function useSetSupplierArchived() {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateSupplierViews()
  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      api.post<void>(`/suppliers/${encodeURIComponent(id)}/${archived ? 'archive' : 'restore'}`),
    onSuccess: (_, { id }) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: supplierKeys.detail(id) }),
        invalidate(),
        // The balance views count current suppliers only.
        queryClient.invalidateQueries({ queryKey: supplierAccountKeys.balanceCounts() }),
      ]),
  })
}

export function useSaveSupplierGroup() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (
      save: { id: string; input: SupplierGroupUpdate } | { id: null; input: SupplierGroupInput },
    ) =>
      save.id
        ? api.put<SupplierGroup>(`/supplier-groups/${encodeURIComponent(save.id)}`, save.input)
        : api.post<SupplierGroup>('/supplier-groups', save.input),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: groupKeys.all }),
        // Group names appear on supplier rows and records.
        queryClient.invalidateQueries({ queryKey: supplierKeys.all }),
      ]),
  })
}

export function useDeleteSupplierGroup() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/supplier-groups/${encodeURIComponent(id)}`),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: groupKeys.all }),
        queryClient.invalidateQueries({ queryKey: supplierKeys.all }),
      ]),
  })
}
